import { installSalesProcessKeyGuard } from './salesProcessKeyGuard';
import type { SubSessionBridge } from './serviceWorkerBridge';
import { installSubSessionBridge } from './serviceWorkerBridge';
import type { SubSessionClaim } from './subSessionClaim';
import { createSubSessionClaim } from './subSessionClaim';
import { getSessionStorage } from './subSessionStorage';
import {
  observeSubSessionControl,
  observeSubSessionRequests,
  reportSubSessionUnsupported,
} from './telemetry';
import {
  CLAIM_CHANNEL_NAME,
  CLAIM_WINDOW_MS_PROPERTY,
  CLIENT_ANSWER_TIMEOUT_MS_PROPERTY,
  DEFAULT_CLAIM_WINDOW_MS,
  SERVICE_WORKER_PATH,
  SERVICE_WORKER_SCOPE,
  SUB_SESSION_SPA_VARIABLE,
  WORKER_ANSWER_TIMEOUT_QUERY_PARAM,
  WORKER_MESSAGE,
} from './wireContract';

export { currentSubSession, onSubSessionChange } from './currentSubSession';

let isInitialised = false;

/** Matched on the path, since the script URL may carry the tuned answer timeout. */
const isSubSessionWorkerScript = (scriptUrl: string): boolean => {
  try {
    return new URL(scriptUrl).pathname.endsWith(SERVICE_WORKER_PATH);
  } catch {
    return false;
  }
};

const isSubSessionWorker = (registration: ServiceWorkerRegistration): boolean =>
  [registration.active, registration.installing, registration.waiting].some(
    (worker) => !!worker && isSubSessionWorkerScript(worker.scriptURL),
  );

/**
 * Kill switch for our worker; other workers on the origin are left alone. Pages it already
 * controls stay controlled until they unload, so it is told to pass everything through first.
 */
const unregisterSubSessionWorker = (container: ServiceWorkerContainer): void => {
  container
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations.filter(isSubSessionWorker).map((registration) => {
          try {
            registration.active?.postMessage({ type: WORKER_MESSAGE.DISABLE });
          } catch {
            // Unregistering still takes the worker away from the next page load.
          }
          return registration.unregister();
        }),
      ),
    )
    .catch(() => undefined);
};

const readPositiveNumberProperty = (name: string): number | null => {
  const value = Number(window.spa_variables?.properties?.[name]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * The worker cannot read site properties, so a tuned answer timeout travels on its
 * script URL. The URL only changes with the value, so the browser does not reinstall the worker.
 */
const serviceWorkerScriptUrl = (): string => {
  const scriptPath = `${process.env.PUBLIC_URL ?? ''}${SERVICE_WORKER_PATH}`;
  const answerTimeoutMs = readPositiveNumberProperty(CLIENT_ANSWER_TIMEOUT_MS_PROPERTY);

  return answerTimeoutMs === null
    ? scriptPath
    : `${scriptPath}?${WORKER_ANSWER_TIMEOUT_QUERY_PARAM}=${answerTimeoutMs}`;
};

const readClaimWindowMs = (): number =>
  readPositiveNumberProperty(CLAIM_WINDOW_MS_PROPERTY) ?? DEFAULT_CLAIM_WINDOW_MS;

const createClaim = (): SubSessionClaim | null => {
  if (typeof BroadcastChannel !== 'function') {
    return null;
  }

  try {
    return createSubSessionClaim({
      channel: new BroadcastChannel(CLAIM_CHANNEL_NAME),
      windowMs: readClaimWindowMs(),
    });
  } catch {
    return null;
  }
};

// A tab restored from the back/forward cache could not answer claims while frozen,
// so a copy of it may have taken its id meanwhile.
const reclaimOnRestore = (bridge: SubSessionBridge): void => {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      bridge.reclaim().catch(() => undefined);
    }
  });
};

/**
 * A registration unregistered while a tab still used it comes back still disabled, and the worker
 * keeps the switch across restarts. Sent before TAKE_CONTROL, which a disabled worker ignores.
 */
const liftKillSwitch = (registration: ServiceWorkerRegistration): void => {
  registration.active?.postMessage({ type: WORKER_MESSAGE.ENABLE });
};

// A hard reload loads the page with no controller, so its requests would bypass the worker.
const requestControlIfUncontrolled = (
  container: ServiceWorkerContainer,
  registration: ServiceWorkerRegistration,
): void => {
  if (!container.controller) {
    observeSubSessionControl(container, Boolean(registration.active));
    registration.active?.postMessage({ type: WORKER_MESSAGE.TAKE_CONTROL });
  }
};

/**
 * Sets up sub-session isolation, so tabs sharing one HTTP session run independent booking
 * flows. Call once, before render. Never blocks rendering; any failure falls back to one session.
 */
export const initSubSession = (isEnabled: boolean): void => {
  // Missing in old browsers, and undefined in insecure contexts.
  const container = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
  if (isInitialised) {
    return;
  }
  if (!container) {
    reportSubSessionUnsupported();
    return;
  }
  isInitialised = true;

  try {
    const storage = isEnabled ? getSessionStorage() : null;
    const claim = storage ? createClaim() : null;

    // Flag off or an API missing: remove a worker of ours left from an earlier visit.
    if (!storage || !claim) {
      reportSubSessionUnsupported();
      unregisterSubSessionWorker(container);
      return;
    }

    installSalesProcessKeyGuard();
    const bridge = installSubSessionBridge({
      claim,
      container,
      search: window.location.search,
      serverResolvedSubSessionId: window.spa_variables?.[SUB_SESSION_SPA_VARIABLE] ?? null,
      storage,
    });
    reclaimOnRestore(bridge);
    observeSubSessionRequests();

    container
      .register(serviceWorkerScriptUrl(), {
        scope: SERVICE_WORKER_SCOPE,
        updateViaCache: 'none',
      })
      .then((registration) => {
        liftKillSwitch(registration);
        requestControlIfUncontrolled(container, registration);
      })
      .catch(() => undefined);
  } catch {
    // Never break the page over this feature.
  }
};
